/**
 * Лёгкие замеры времени сбора для диагностики на реальных устройствах (параметр timings).
 * Результат попадает в lfidData.fpTimings. Без параметра коллекторы получают noopLap и ничего не замеряют.
 */

/** Отметка этапа внутри коллектора, когда замеры выключены. */
export const noopLap = () => {};

// Миллисекунды с одним знаком: Chrome огрубляет performance.now() до 0,1 мс, Safari — до 1 мс.
const round = ms => Math.round(ms * 10) / 10;

/**
 * @param {Window} window
 * @param {number} entry performance.now() при входе в fingerprint()
 * @param {number} start performance.now() в начале сбора (точка отсчёта EXECUTION_TIME)
 */
export function createTimings(window, entry, start) {
    const now = () => performance.now();
    const document = window?.document;
    const since = () => round(now() - start);
    const data = {
        // v2: добавлен visRel (в 1.3.0 был и checks — контрольная отрисовка Canvas, убрана в 1.3.1).
        v: 2,
        // С начала загрузки страницы до старта сбора и от входа в fingerprint() до старта сбора.
        nav: round(start),
        pre: round(start - entry),
        rs: document?.readyState ?? null,
        vis: [document?.visibilityState ?? null],
        sync: {},
        end: {},
        steps: {},
    };

    /**
     * Возвращает функцию-отметку: каждый вызов записывает время с предыдущей отметки
     * (для первой — с момента создания) в steps[group][step].
     * @param {string} group
     */
    const laps = group => {
        let last = now();
        return step => {
            const current = now();
            if (!data.steps[group]) {
                data.steps[group] = {};
            }
            data.steps[group][step] = round(current - last);
            last = current;
        };
    };

    return {
        /**
         * Запускает коллектор, записывает длительность его синхронной части (sync) и момент завершения (end).
         * @param {string} name
         * @param {function(function(string)): Promise} run
         */
        launch(name, run) {
            const before = now();
            const promise = run(laps(name));
            data.sync[name] = round(now() - before);
            promise.then(() => {
                data.end[name] = since();
            }, () => {
                data.end[name] = since();
            });
            return promise;
        },
        /**
         * Записывает момент завершения промиса, запущенного вне launch().
         * @param {string} name
         * @param {Promise} promise
         */
        track(name, promise) {
            promise.then(() => {
                data.end[name] = since();
            }, () => {
                data.end[name] = since();
            });
        },
        /** @param {string} name */
        mark(name) {
            data[name] = since();
        },
        /**
         * Записывает document.visibilityState в data[name] (например, в момент release:
         * от него зависит, ждут ли датчики, см. fpCollectDevices.js).
         * @param {string} name
         */
        markVisibility(name) {
            data[name] = document?.visibilityState ?? null;
        },
        finish() {
            data.vis.push(document?.visibilityState ?? null);
            return data;
        },
    };
}
